import { useState, type FormEvent } from 'react';
import { useSession } from '../App';
import type { ApiError, BillingOverview, Plan } from '../api';
import { PaymentWall, type WallChoice } from '../components/PaymentWall';
import {
  Card,
  ErrorNotice,
  Loading,
  Notice,
  PageHeader,
  PaymentBadge,
  StatusBadge,
  useAsync,
} from '../components/ui';
import { featureLines, formatDate, formatMoney, periodLabel } from '../format';

type Interval = 1 | 12;
type Choice = WallChoice;

export function BillingPage() {
  const { client } = useSession();
  const state = useAsync(() => Promise.all([client.billing(), client.plans()]), [client]);
  const [interval, setInterval] = useState<Interval>(1);
  const [choice, setChoice] = useState<Choice | null>(null);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={() => void state.reload()} />;
  const [billing, { plans }] = state.data!;

  const subscription = billing.subscription;
  const currentIndex = Math.max(0, plans.findIndex((plan) => plan.key === subscription.plan));
  const currentPlan = plans[currentIndex];
  const prepaidRunning =
    subscription.renewal === 'manual' &&
    subscription.currentPeriodEnd !== null &&
    new Date(subscription.currentPeriodEnd) > new Date();
  const saving = yearlySaving(plans);

  return (
    <>
      <PageHeader
        eyebrow="Plan & billing"
        title="Your plan"
        description="Choose a plan, extend it, and see what you have paid for."
      />

      {billing.checkout.testMode && (
        <Notice tone="warning">
          <strong>Test mode.</strong> Payments go to the PayU sandbox — no real money moves. Use PayU's test
          BLIK codes or test cards.
        </Notice>
      )}

      <CurrentPlan billing={billing} planName={currentPlan?.name ?? subscription.plan} />

      <div className="section-head">
        <h2>Plans</h2>
        <div className="segmented" role="tablist" aria-label="Billing period">
          <button type="button" role="tab" aria-selected={interval === 1} className={interval === 1 ? 'on' : ''} onClick={() => setInterval(1)}>
            Monthly
          </button>
          <button type="button" role="tab" aria-selected={interval === 12} className={interval === 12 ? 'on' : ''} onClick={() => setInterval(12)}>
            Yearly{saving ? <span className="save">save {saving}%</span> : null}
          </button>
        </div>
      </div>

      <div className="grid plans">
        {plans.map((plan, index) => {
          const price = plan.prices.find((candidate) => candidate.intervalMonths === interval);
          const isCurrent = plan.key === subscription.plan;
          const features = featureLines(plan.features);

          let action: { label: string; choice?: Choice; note?: string };
          if (plan.isDefault) {
            action = { label: isCurrent ? 'Current plan' : 'Included with every account' };
          } else if (!price) {
            action = { label: `No ${interval === 12 ? 'yearly' : 'monthly'} option` };
          } else if (!billing.checkout.available) {
            action = { label: 'Not available yet', note: 'Online payments are switched off right now.' };
          } else if (isCurrent && subscription.renewal === 'manual') {
            action = { label: `Extend by 1 ${periodLabel(interval)}`, choice: { plan, price, kind: 'extend' } };
          } else if (isCurrent) {
            action = { label: 'Current plan', note: 'Provided by a licence or by DeskTouch support.' };
          } else if (index > currentIndex) {
            action = {
              label: currentPlan?.isDefault ? `Get ${shortName(plan.name)}` : `Upgrade to ${shortName(plan.name)}`,
              choice: { plan, price, kind: currentPlan?.isDefault ? 'buy' : 'upgrade' },
            };
          } else {
            action = {
              label: 'Not available',
              note: prepaidRunning
                ? `You can switch after ${formatDate(subscription.currentPeriodEnd)}.`
                : 'Your current plan includes everything here.',
            };
          }

          return (
            <section key={plan.key} className={`plan ${isCurrent ? 'current' : ''} ${plan.key === 'pro' ? 'featured' : ''}`}>
              {isCurrent && <span className="ribbon">Current</span>}
              <h3>{shortName(plan.name)}</h3>
              <p className="muted plan-desc">{plan.description}</p>
              <p className="price">
                {plan.isDefault ? (
                  <>
                    <strong>{formatMoney(0, price?.currency ?? 'PLN')}</strong>
                    <span>forever</span>
                  </>
                ) : price ? (
                  <>
                    <strong>{formatMoney(price.amountMinor, price.currency)}</strong>
                    <span>/ {periodLabel(price.intervalMonths)}</span>
                  </>
                ) : (
                  <span>—</span>
                )}
              </p>
              {price && interval === 12 && !plan.isDefault && (
                <p className="fine">That is {formatMoney(Math.round(price.amountMinor / 12), price.currency)} a month.</p>
              )}
              <button
                type="button"
                className={action.choice ? 'primary block' : 'block'}
                disabled={!action.choice}
                onClick={() => action.choice && setChoice(action.choice)}
              >
                {action.label}
              </button>
              {action.note && <p className="fine center">{action.note}</p>}
              <ul className="features">
                {features.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <PaymentHistory billing={billing} />
      <RedeemLicence onRedeemed={() => void state.reload()} />

      <p className="fine payment-note">
        Payments are processed by PayU S.A. DeskTouch never sees or stores your card details. Paid plans do not
        renew automatically — you choose when to extend. Prices include VAT.
      </p>

      <PaymentWall
        choice={choice}
        billing={billing}
        onClose={() => setChoice(null)}
        onPaid={() => void state.reload()}
      />
    </>
  );
}

function CurrentPlan({ billing, planName }: { billing: BillingOverview; planName: string }) {
  const subscription = billing.subscription;
  const free = subscription.status === 'none';
  let detail: string;
  if (free) detail = 'You are on the free plan. Upgrade any time — there is nothing to cancel.';
  else if (subscription.renewal === 'manual')
    detail = `Paid through ${formatDate(subscription.currentPeriodEnd)}. It does not renew by itself; extend it whenever you like.`;
  else if (subscription.currentPeriodEnd) detail = `Active until ${formatDate(subscription.currentPeriodEnd)}.`;
  else detail = 'Active with no end date.';
  if (subscription.organizationId) detail += ' Provided through your organization.';

  return (
    <Card className="current-plan">
      <div className="current-plan-row">
        <div>
          <p className="eyebrow">Current plan</p>
          <p className="plan-name">{planName}</p>
          <p className="muted">{detail}</p>
        </div>
        {!free && <StatusBadge status={subscription.status} />}
      </div>
    </Card>
  );
}

function PaymentHistory({ billing }: { billing: BillingOverview }) {
  return (
    <Card title="Payment history">
      {billing.payments.length === 0 ? (
        <p className="muted">No payments yet.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th>Covers</th>
              </tr>
            </thead>
            <tbody>
              {billing.payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.createdAt)}</td>
                  <td>
                    {payment.description}
                    {payment.method && <span className="fine method-note">{METHOD_LABELS[payment.method]}</span>}
                  </td>
                  <td className="num">{formatMoney(payment.amountMinor, payment.currency)}</td>
                  <td>
                    <PaymentBadge status={payment.status} />
                  </td>
                  <td>
                    {payment.periodEnd ? (
                      `until ${formatDate(payment.periodEnd)}`
                    ) : payment.status === 'pending' && payment.redirectUrl ? (
                      <a href={payment.redirectUrl}>Finish payment</a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RedeemLicence({ onRedeemed }: { onRedeemed: () => void }) {
  const { client } = useSession();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = await client.redeemLicense(code.trim().toUpperCase());
      setDone(result.plan);
      setCode('');
      onRedeemed();
    } catch (nextError) {
      setError(nextError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Have a licence key?">
      <form className="inline-form" onSubmit={submit}>
        <input
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="DT-XXXX-XXXX-XXXX"
          aria-label="Licence key"
          autoComplete="off"
          spellCheck={false}
          maxLength={64}
        />
        <button type="submit" disabled={busy || code.trim().length < 8}>
          {busy ? 'Checking…' : 'Redeem'}
        </button>
      </form>
      {done && <Notice tone="success">Licence redeemed — your account now has {done}.</Notice>}
      {error ? (
        (error as ApiError).code === 'not_found' || (error as ApiError).status === 400 ? (
          <Notice tone="danger">That key is not valid, has expired, or has already been used.</Notice>
        ) : (
          <ErrorNotice error={error} />
        )
      ) : null}
    </Card>
  );
}

const METHOD_LABELS: Record<string, string> = { blik: 'BLIK', card: 'Card', hosted: 'PayU' };

/** "DeskTouch Pro" → "Pro" where the brand is already obvious. */
function shortName(name: string): string {
  return name.replace(/^DeskTouch\s+/i, '');
}

/** Rounded percentage a year costs less than twelve months, across plans. */
function yearlySaving(plans: Plan[]): number | null {
  const savings = plans
    .map((plan) => {
      const monthly = plan.prices.find((price) => price.intervalMonths === 1);
      const yearly = plan.prices.find((price) => price.intervalMonths === 12);
      return monthly && yearly ? 1 - yearly.amountMinor / (monthly.amountMinor * 12) : null;
    })
    .filter((value): value is number => value !== null && value > 0);
  return savings.length ? Math.round(Math.min(...savings) * 100) : null;
}
